import { useCallback, useEffect, useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

import {
	getAvailableLicenseExtensions,
	getMyLicensePurchases,
} from "~/modules/sales/licenseExtensions/services";
import type {
	AvailableLicenseExtension,
	LicenseExtensionPurchase,
} from "~/modules/sales/licenseExtensions/types";

import { LicenseOfferCard } from "./LicenseOfferCard";
import { PurchaseHistoryTable } from "./PurchaseHistoryTable";

type Props = {
	termStart: string;
	termEnd: string;
};

export const LicenseExtensions: React.FC<Props> = ({ termStart, termEnd }) => {
	const [offers, setOffers] = useState<AvailableLicenseExtension[]>([]);
	const [purchases, setPurchases] = useState<LicenseExtensionPurchase[]>([]);
	const [loadingOffers, setLoadingOffers] = useState(true);
	const [loadingPurchases, setLoadingPurchases] = useState(true);
	const [historyOpen, setHistoryOpen] = useState(false);

	const fetchOffers = useCallback(() => {
		setLoadingOffers(true);
		getAvailableLicenseExtensions()
			.then(setOffers)
			.finally(() => setLoadingOffers(false));
	}, []);

	const fetchPurchases = useCallback(() => {
		setLoadingPurchases(true);
		getMyLicensePurchases()
			.then(setPurchases)
			.finally(() => setLoadingPurchases(false));
	}, []);

	useEffect(() => {
		fetchOffers();
		fetchPurchases();
	}, [fetchOffers, fetchPurchases]);

	const handlePurchased = useCallback(() => {
		fetchPurchases();
	}, [fetchPurchases]);

	return (
		<div className="flex flex-col gap-4">
			{/* Available packages */}
			<div className="flex flex-col gap-3">
				<h3 className="font-bold text-xl text-gray-800">
					Licenças Avulsas Disponíveis
				</h3>
				<p className="text-sm text-gray-500 -mt-1">
					Adicione licenças extras ao seu plano pelo período restante do ciclo
					atual.
				</p>

				{loadingOffers ? (
					<div className="flex justify-center py-6">
						<div
							className="h-8 w-8 border-[6px] rounded-full
                border-r-gpa-blue-50 border-gpa-blue-500 animate-spin"
						/>
					</div>
				) : offers.length === 0 ? (
					<p className="text-sm text-center text-gpa-gray-800 border-t border-gpa-gray-500 pt-4">
						Nenhum pacote disponível no momento.
					</p>
				) : (
					<div className="flex flex-col gap-3">
						{offers.map((ext) => (
							<LicenseOfferCard
								key={ext.id}
								extension={ext}
								onPurchased={handlePurchased}
							/>
						))}
					</div>
				)}
			</div>

			{/* Purchase history — collapsible */}
			<div className="border border-slate-200 rounded-xl overflow-hidden">
				<button
					type="button"
					className="w-full flex items-center justify-between px-5 py-4
            bg-gpa-gray-50 hover:bg-gpa-gray-100 transition-colors duration-150"
					onClick={() => setHistoryOpen((o) => !o)}
				>
					<div className="flex items-center gap-2">
						<span className="font-semibold text-sm">Histórico de compras</span>
						{!loadingPurchases && purchases.length > 0 ? (
							<span
								className="text-xs bg-gpa-blue-50 text-gpa-blue-500 font-semibold
                  px-2 py-0.5 rounded-full"
							>
								{purchases.length}
							</span>
						) : null}
					</div>
					{historyOpen ? (
						<FiChevronUp size={18} className="text-gray-500" />
					) : (
						<FiChevronDown size={18} className="text-gray-500" />
					)}
				</button>

				{historyOpen ? (
					<div className="p-5">
						{loadingPurchases ? (
							<div className="flex justify-center py-6">
								<div
									className="h-8 w-8 border-[6px] rounded-full
                    border-r-gpa-blue-50 border-gpa-blue-500 animate-spin"
								/>
							</div>
						) : (
							<PurchaseHistoryTable purchases={purchases} />
						)}
					</div>
				) : null}
			</div>
		</div>
	);
};
